import { Menu, Transition } from '@headlessui/react';
import Link from 'next/link';
import { forwardRef, Fragment, HTMLAttributes, MouseEventHandler, ReactElement, ReactNode, RefObject, useEffect, useState } from 'react';
import { Modifier, usePopper } from 'react-popper';
import { Portal } from 'react-portal';
import { Icon, IconDefinition } from 'stylus-ui/Icon';
import { cn } from 'utils/cn';
import Button from 'v2/components/atoms/Button';

const Card = forwardRef(({ className, ...props }: HTMLAttributes<HTMLDivElement>, ref: RefObject<HTMLDivElement>) => {
  return <div ref={ref} className={cn('ctx-menu_card', className)} {...props} />;
});

const Divider = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return <div className={cn('ctx-menu_divider', className)} {...props} />;
};

export interface ItemProps {
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLElement>;
  href?: string;
  icon?: IconDefinition;
  disabled?: boolean;
  className?: string;
  selected?: boolean;
  dataTestID?: string;
  noHighlight?: boolean;
  description?: string;
}

const Item = ({ children, onClick, icon, selected, dataTestID, noHighlight, href, description, disabled, className }: ItemProps) => {
  return (
    <Menu.Item disabled={disabled}>
      {({ active }) => {
        const highlighted = active || selected;

        if (href) {
          return (
            <Link
              href={href}
              onClick={onClick}
              legacyBehavior
              className={cn(
                'h-12',
                {
                  '!cursor-default !bg-transparent': !!noHighlight
                },
                className
              )}
            >
              <a data-testid={dataTestID} href={href}>
                <div
                  className={cn('ctx-menu_item flex flex-col !items-start', {
                    'ctx-menu_item--active': highlighted,
                    'ctx-menu_item--icon': icon !== undefined,
                    '!cursor-default !bg-transparent': !!noHighlight,
                    '!pb-px': !!description
                  })}
                >
                  <div>
                    {icon && (
                      <Icon
                        className={cn('ctx-menu_icon', {
                          'ctx-menu_icon--active': highlighted
                        })}
                        icon={icon}
                        fixedWidth
                      />
                    )}

                    <span>{children}</span>
                  </div>
                  {description && (
                    <div className='mb-1 ml-7 mr-4 select-none text-xs font-normal leading-4 text-slate-400'>{description}</div>
                  )}
                </div>
              </a>
            </Link>
          );
        }
        return (
          <div onClick={onClick} data-testid={dataTestID}>
            <div
              className={cn(
                'ctx-menu_item flex flex-col !items-start',
                {
                  'ctx-menu_item--active': highlighted,
                  'ctx-menu_item--icon': icon !== undefined,
                  '!cursor-default !bg-transparent': !!noHighlight,
                  '!pb-0': !!description
                },
                className
              )}
            >
              <div>
                {icon && (
                  <Icon
                    className={cn('ctx-menu_icon', {
                      'ctx-menu_icon--active': highlighted
                    })}
                    icon={icon}
                    fixedWidth
                  />
                )}

                <span>{children}</span>
              </div>
              {description && <div className='mb-1 ml-7 mr-4 select-none text-xs font-normal leading-4 text-slate-400'>{description}</div>}
            </div>
          </div>
        );
      }}
    </Menu.Item>
  );
};

const Title = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('ctx-menu_title', className)} {...props} />;
};

/**
 * Placement casing is designed for familiarity and compatibility with Popper.
 */
export type Placement = 'bottom-start' | 'bottom' | 'bottom-end' | 'top-start' | 'top' | 'top-end' | 'right' | 'right-end';

/**
 * Based on Popper element placement, we need to provide a proper transformation
 * origin for the menu's transition animation. These strings represent Tailwind
 * classes.
 */
const origins: Record<Placement, string> = {
  'bottom-start': 'origin-top-left',
  bottom: 'origin-top',
  'bottom-end': 'origin-top-right',
  'top-start': 'origin-bottom-left',
  top: 'origin-bottom',
  'top-end': 'origin-bottom-right',
  right: 'origin-right',
  'right-end': 'origin-bottom-left'
};

export type Variant = 'light' | 'dark' | 'indigo';

export interface ContextMenuProps {
  className?: string;
  itemsClassName?: string;
  cardClassName?: string;
  button?: ReactNode | (({ open }) => ReactNode) | ReactElement;
  children: ReactNode;
  placement?: Placement;
  variant?: Variant;
  zValue?: number;
  toggleBtnAriaLabel?: string;
  dataTestID?: string;
  modifiers?: readonly Partial<Modifier<unknown, object>>[];
  /**
   * Controls how many pixels will be between the card and button.
   */
  offset?: number | [number, number];
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
}

const ContextMenuRoot = ({
  className,
  itemsClassName = '',
  cardClassName = '',
  button,
  children,
  dataTestID,
  variant = 'dark',
  zValue = 1000000,
  placement = 'bottom-end',
  offset = 8,
  toggleBtnAriaLabel = 'toggle menu',
  modifiers = [],
  onOpenChange,
  disabled = false
}: ContextMenuProps) => {
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>();
  const [popperElement, setPopperElement] = useState<HTMLDivElement>();
  const popperInstance = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: offset[0] ? [offset[0], offset[1]] : [0, offset]
        }
      },
      ...modifiers
    ]
  });

  return (
    <Menu as='div' className={className}>
      {({ open }) => {
        useEffect(() => {
          onOpenChange?.(open);
        }, [open]);
        useEffect(() => {
          return () => onOpenChange?.(false);
        }, []);
        // `button` can be a Button. If so, we don't want to wrap it in a Menu.Button, otherwise we end up with html
        // <button><button>...</button></button> which is invalid.
        return (
          <>
            {typeof button === 'object' && 'type' in button && button?.type === Button ? (
              button
            ) : (
              <Menu.Button
                ref={setReferenceElement}
                className='inline-block bg-transparent p-0'
                data-testid={dataTestID}
                aria-label={toggleBtnAriaLabel}
              >
                {typeof button === 'function' ? button({ open }) : button}
              </Menu.Button>
            )}
            {!disabled && (
              <Portal>
                <Transition>
                  {/* This is used an invisible overlay that prevents interaction with outside nodes. */}
                  {open && <div className='fixed inset-0' />}
                  <Menu.Items
                    ref={setPopperElement}
                    style={{ zIndex: zValue, ...popperInstance.styles.popper }}
                    className={cn(itemsClassName, `ctx-menu--${variant}`, 'focus-visible:outline-none')}
                    {...popperInstance.attributes.popper}
                  >
                    <Transition.Child
                      as={Fragment}
                      enter='transition ease-out duration-100'
                      enterFrom='transform opacity-0 scale-95'
                      enterTo='transform opacity-100 scale-100'
                      leave='transition ease-in duration-75'
                      leaveFrom='transform opacity-100 scale-100'
                      leaveTo='transform opacity-0 scale-95'
                    >
                      <Card className={cn([origins[placement], cardClassName])}>{children}</Card>
                    </Transition.Child>
                  </Menu.Items>
                </Transition>
              </Portal>
            )}
          </>
        );
      }}
    </Menu>
  );
};

export const ContextMenu = Object.assign(ContextMenuRoot, { Card, Divider, Item, Title });
